<script setup lang="ts">
import { SliderRange, SliderRoot, SliderThumb, SliderTrack } from 'reka-ui';
import { computed } from 'vue';
import { cn } from '@/lib/utils';

const props = withDefaults(
    defineProps<{
        label: string;
        modelValue: number;
        min?: number;
        max?: number;
        step?: number;
        disabled?: boolean;
        showValue?: boolean;
        /** When false, hides the visible label (keeps screen-reader text if `label` is non-empty). */
        showLabel?: boolean;
        /** Optional formatter for the value chip (defaults to raw number). */
        formatValue?: (value: number) => string;
        /** Extra classes on the outer wrapper (spacing, touch targets, etc.). */
        wrapperClass?: string;
        /** Extra classes merged into the slider row (height, padding). */
        sliderClass?: string;
    }>(),
    {
        min: 0,
        max: 100,
        step: 1,
        disabled: false,
        showValue: true,
        showLabel: true,
    },
);

const emit = defineEmits<{
    'update:modelValue': [value: number];
}>();

const displayValue = computed(() => {
    if (props.formatValue) {
        return props.formatValue(props.modelValue);
    }

    return String(props.modelValue);
});

function onSliderUpdate(v: number[] | undefined): void {
    const n = v?.[0];

    if (n !== undefined) {
        emit('update:modelValue', n);
    }
}
</script>

<template>
    <div :class="cn('space-y-2', props.wrapperClass)">
        <span v-if="!showLabel && !showValue && label" class="sr-only">
            {{ label }}
        </span>
        <div
            v-if="showLabel || showValue"
            :class="
                cn(
                    'flex items-center gap-3',
                    showLabel && showValue
                        ? 'justify-between'
                        : showValue && !showLabel
                          ? 'justify-end'
                          : 'justify-start',
                )
            "
        >
            <span
                v-if="showLabel"
                class="text-sm font-medium text-gray-800 dark:text-gray-200"
            >
                {{ label }}
            </span>
            <span
                v-if="showValue"
                class="rounded-md bg-brand-50 px-2 py-0.5 font-mono text-xs text-brand-800 tabular-nums dark:bg-brand-950 dark:text-brand-200"
            >
                {{ displayValue }}
            </span>
        </div>
        <SliderRoot
            :class="
                cn(
                    'relative flex h-9 w-full touch-none items-center select-none',
                    props.sliderClass,
                )
            "
            :model-value="[modelValue]"
            :min="min"
            :max="max"
            :step="step"
            :disabled="disabled"
            :aria-label="!showLabel && label ? label : undefined"
            @update:model-value="onSliderUpdate"
        >
            <SliderTrack
                :class="
                    cn(
                        'relative h-2.5 w-full grow overflow-hidden rounded-full',
                        'bg-gray-200/90 dark:bg-gray-700/90',
                        'shadow-inner',
                    )
                "
            >
                <SliderRange
                    class="absolute h-full rounded-full bg-brand-500 shadow-sm dark:bg-brand-500"
                />
            </SliderTrack>
            <SliderThumb
                :class="
                    cn(
                        'block size-5 cursor-grab rounded-full border-2 border-brand-600 bg-background shadow-md',
                        'transition-[box-shadow,transform] duration-150',
                        'hover:border-brand-500 hover:shadow-lg',
                        'outline-none focus-visible:ring-[3px] focus-visible:ring-brand-500/45 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                        'active:scale-[0.97] active:cursor-grabbing',
                        'disabled:pointer-events-none disabled:opacity-40',
                    )
                "
            />
        </SliderRoot>
    </div>
</template>
